import { DOMAdapter } from 'pixi.js';

import { createValidator, retry } from './libs/retry';

/**
 * We get fast WebGL when it is hardware (GPU) accelerated.
 */
export const FAST_WEBGL = 'fast';
/**
 * We get slow WebGL when it is rendered using software.
 */
export const SLOW_WEBGL = 'slow';
type WebGLSupportedType = typeof FAST_WEBGL | typeof SLOW_WEBGL;

type WebGLSupportedState = {
    fast: boolean | undefined;
    slow: boolean | undefined;
};

const WebGLSupported: WebGLSupportedState = {
    fast: undefined,
    slow: undefined,
};

/**
 * Returns true if the current environment supports WebGL. We can either test for fast or slow support. By default, we
 * return the cached result. If you want to force a retest, pass true as the second parameter.
 * @param force - If true, we'll force a retest of WebGL support.
 */
export function isWebGLSupported(type: WebGLSupportedType = FAST_WEBGL, force = false): boolean {
    if (typeof WebGLSupported[type] === 'undefined' || force) {
        if (typeof WebGLSupported[type] === 'boolean' && WebGLSupported[type]) {
            return true;
        }
        WebGLSupported[type] = (() => {
            const contextOptions = {
                stencil: true,
                failIfMajorPerformanceCaveat: type === FAST_WEBGL,
            };
            try {
                if (!DOMAdapter.get().getWebGLRenderingContext()) {
                    return false;
                }
                const canvas = DOMAdapter.get().createCanvas();
                const gl = canvas.getContext('webgl', contextOptions) as WebGLRenderingContext;
                const success = !!(gl && gl.getContextAttributes()?.stencil);

                if (gl) {
                    const loseContext = gl.getExtension('WEBGL_lose_context');

                    if (loseContext) {
                        loseContext.loseContext();
                    }
                }

                return success;
            } catch (e) {
                console.log('error', e);
                return false;
            }
        })();
    }
    // It will always be boolean at this point, but TypeScript can't figure it out.
    return !!WebGLSupported[type];
}

/**
 * Confirm that WebGL is supported. It's possible for isWebGLSupported to be called before WebGL is ready.
 * When that's the case, we'll force retry a few times to see if WebGL is supported. We force retry because we cache
 * the result of isWebGLSupported. If we don't force retry, we'll always get the same cached result which would be
 * false.
 */
export async function confirmWebGLSupport() {
    if (isWebGLSupported(SLOW_WEBGL, false)) {
        return;
    }
    const validator = createValidator((value) => value === true);
    const success = await retry(3, validator(isWebGLSupported), SLOW_WEBGL, true);
    if (!success) {
        new Error('WebGL is not supported. Unable to render slice.');
    }
}
